import { sanitizeForLog } from '@/constants'
import { Box, Button, Typography } from '@mui/material'
import { useState } from 'react'
import { uploadCsiFile } from '../../../../api/adminTAN'
import { returnsFvuGenerate } from '../../../../api/returnsStep'
import { CloseIcon, UploadIcon } from '../../../../assets/icons/index'

function GenerateFVU({
  showSnackbar,
  setgenerateFVUDialogOpen,
  defaultSearchCOList,
  currentPage,
  itemsPerPage,
  tanMasterUuid,
}) {
  const [uploadedFile, setUploadedFile] = useState(null)
  const [errors, setErrors] = useState({})

  const handleDeleteFile = () => {
    setUploadedFile(null)
    if (errors.csiFile) {
      setErrors(prev => ({
        ...prev,
        csiFile: '',
      }))
    }
  }

  const handleFileSelect = event => {
    const file = event.target.files[0]
    if (!file) return

    const fileExtension = file.name.split('.').pop().toLowerCase()

    if (fileExtension !== 'csi') {
      setUploadedFile(null)
      setErrors(prev => ({
        ...prev,
        csiFile: 'Only .csi files are allowed',
      }))
      return
    }

    setUploadedFile(file)
    setErrors(prev => ({ ...prev, csiFile: '' }))
  }

  const handleFileUpload = async () => {
    if (!uploadedFile) {
      setErrors(prev => ({
        ...prev,
        csiFile: 'Please select a CSI file before uploading',
      }))
      return
    }

    try {
      const response = await uploadCsiFile(uploadedFile)
      // CWE-117: sanitize untrusted upload response before logging (strip CR/LF).
      console.log(sanitizeForLog(response))
      const {
        entityTan,
        entityName,
        returnType,
        formType,
        quarter,
        financialYear,
        tanUuid,
      } = defaultSearchCOList || {}
      const payload = {
        pageFilter: {
          includeCount: false,
          currentPage: currentPage,
          pageSize: itemsPerPage,
          sortField: 'entityTAN',
          sortOrder: 'DESC',
        },
        tanUuid: tanUuid,
        entityTAN: entityTan,
        entityName,
        returnType: returnType?.toUpperCase(),
        formType: formType,
        quarter,
        financialYear: financialYear,
      }

      if (response.success) {
        const result = await returnsFvuGenerate(payload, response.data)
        if (result?.data?.success) {
          setgenerateFVUDialogOpen(false)
          showSnackbar('CSI file uploaded successfully!', 'success')
        } else {
          showSnackbar(result?.data?.errors.pop()?.message, 'error')
        }
      }
    } catch (error) {
      showSnackbar(error.message, 'error')
      console.error('Upload failed:', error)
    }
  }

  return (
    <>
      <Box sx={{ flex: { xs: 'none', sm: 1 }, order: { xs: 2, sm: 1 } }}>
        {/* Show attached file if exists (edit mode or new upload) */}
        {uploadedFile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontWeight: 500,
                fontSize: { xs: '12px', sm: '14px' },
              }}
            >
              Attached File:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#1976d2',
                fontSize: { xs: '12px', sm: '14px' },
                wordBreak: 'break-all',
              }}
            >
              {uploadedFile ? uploadedFile.name : null}
            </Typography>
            <Button
              onClick={handleDeleteFile}
              sx={{
                p: 0.5,
                color: '#666',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: '#ffebee',
                  color: '#f44336',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: '18px' }} />
            </Button>
          </Box>
        )}

        {/* File upload area - show when no file is attached */}
        {!uploadedFile && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              border: errors.csiFile ? '2px dashed #f44336' : '2px dashed #ccc',
              borderRadius: 1,
              p: '22px',
              backgroundColor: '#ebebeb',
            }}
          >
            <UploadIcon />
            <Typography
              variant="body2"
              sx={{
                color: '#000',
                fontSize: { xs: '11px', sm: '14px' },
                textAlign: { xs: 'center', sm: 'left' },
                lineHeight: { xs: 1.3, sm: 1.4 },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: 'block', sm: 'inline' } }}
              >
                Drag & drop files in this area or
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: 'block', sm: 'inline' } }}
              >
                <Button
                  component="label"
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    minWidth: 'auto',
                    color: 'var(--forbrand-color)',
                    textDecoration: 'underline',
                    fontSize: { xs: '11px', sm: '14px' },
                    fontWeight: { xs: 600, sm: 400 },
                    px: 0.5,
                  }}
                >
                  Browse Files
                  <input
                    type="file"
                    hidden
                    accept=".csi"
                    onChange={handleFileSelect}
                  />
                </Button>
              </Box>
            </Typography>
          </Box>
        )}

        {errors.csiFile && (
          <Typography
            variant="body2"
            sx={{
              color: '#f44336',
              mt: 1,
              fontSize: { xs: '11px', sm: '12px' },
            }}
          >
            {errors.csiFile}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          margin: '16px 0',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{ fontSize: '14px', color: '#E0301E', fontWeight: 400 }}
          variant="h6"
        >
          Kindly generate Regular file before generating FVU file.
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: 'var(--forbrand-bg-variant)',
            color: 'var(--forbrand-brand-varient)',
            px: '16px',
            py: '8px',
            height: '32px',
            textTransform: 'none',
            fontWeight: 500,
            marginTop: '1px',
          }}
          onClick={handleFileUpload}
        >
          Upload
        </Button>
      </Box>
    </>
  )
}

export default GenerateFVU
